import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { getCoderHistory } from '../../../Redux/CoderApis/main.actions';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import './style.css';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  heading: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: '600 !important',
    color: '#1a252f',
    paddingBottom: '1.2rem !important',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: 'calc(100vh - 240px)',
    overflowY: 'auto',
    paddingRight: '6px',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#cbd5e0',
      borderRadius: '4px',
      '&:hover': {
        background: '#a0aec0',
      },
    },
  },
  historyCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '14px 18px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: '#139696',
      boxShadow: '0 4px 10px rgba(19, 150, 150, 0.12)',
    },
  },
  historyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    paddingBottom: '6px',
    borderBottom: '1px solid #edf2f7',
  },
  headerLeft: {
    fontWeight: 600,
    fontSize: '15px',
    color: '#2d3748',
  },
  headerStatus: {
    fontWeight: 600,
    fontSize: '13px',
    color: '#c05621',
    backgroundColor: '#feebc8',
    padding: '3px 10px',
    borderRadius: '4px',
  },
  historyLine: {
    display: 'flex',
    alignItems: 'baseline',
    marginTop: '4px',
    fontSize: '14px',
    lineHeight: '1.5',
  },
  lineLabel: {
    fontWeight: 700,
    minWidth: '65px',
    color: '#2d3748',
  },
  lineValue: {
    fontWeight: 500,
    color: '#4a5568',
  },
  modifier: {
    color: '#139696',
    cursor: 'help',
    borderBottom: '1px dotted #139696',
  },
  noData: {
    textAlign: 'center',
    color: '#718096',
    padding: '2rem',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: '2rem',
  },
}));

const CLAIM_STATUS_MAP = {
  1: 'Visit Completed',
  2: 'Claim Created',
  3: 'Claim Filed',
  4: 'Claim Rejected',
  5: 'Claim Denied',
  6: 'Secondary Ready',
  7: 'Secondary Pending',
  8: 'Tertiary Ready',
  9: 'Tertiary Pending',
  10: 'Patient Pending',
  11: 'Closed',
  12: 'Clarification Opened',
  13: 'Clarification Answered',
  14: 'Claim On Hold',
};

const getStatusStyle = (status) => {
  switch (Number(status)) {
    case 1:
      return { color: '#065f46', backgroundColor: '#d1fae5' };
    case 2:
      return { color: '#1d4ed8', backgroundColor: '#dbeafe' };
    case 3:
      return { color: '#0f766e', backgroundColor: '#ccfbf1' };
    case 4:
      return { color: '#b91c1c', backgroundColor: '#fee2e2' };
    case 5:
      return { color: '#991b1b', backgroundColor: '#fecaca' };
    case 6:
      return { color: '#4338ca', backgroundColor: '#e0e7ff' };
    case 7:
      return { color: '#b45309', backgroundColor: '#fef3c7' };
    case 8:
      return { color: '#6b21a8', backgroundColor: '#f3e8ff' };
    case 9:
      return { color: '#d97706', backgroundColor: '#fef3c7' };
    case 10:
      return { color: '#c2410c', backgroundColor: '#ffedd5' };
    case 11:
      return { color: '#047857', backgroundColor: '#d1fae5' };
    case 12:
      return { color: '#0e7490', backgroundColor: '#cffafe' };
    case 13:
      return { color: '#065f46', backgroundColor: '#d1fae5' };
    case 14:
      return { color: '#78350f', backgroundColor: '#fef3c7' };
    default:
      return { color: '#4b5563', backgroundColor: '#f3f4f6' };
  }
};

function CoderHistoryLib(props) {
  const classes = useStyles();

  useEffect(() => {
    const patientId =
      props.rowInfo?.patientId ||
      props.coderData?.coderInfo?.response?.data?.patientId ||
      props.coderData?.coderInfo?.response?.data?.patient?.id ||
      props.coderData?.coderInfo?.data?.patientId ||
      props.coderData?.coderInfo?.data?.patient?.id;
    const clinicId =
      props.rowInfo?.clinicId ||
      props.rowInfo?.facilityId ||
      sessionStorage.getItem('clinicId');

    if (patientId && clinicId && props.getCoderHistory) {
      props.getCoderHistory({
        applicationId: props.applicationId || 7,
        data: {
          patientId: Number(patientId),
          clinicId: Number(clinicId),
        },
      });
    }
  }, [props.rowInfo, props.coderData?.coderInfo, props.applicationId]);

  const resPayload =
    props.coderHistoryData?.response || props.coderHistoryData;
  const historyData = resPayload;

  const visits =
    resPayload?.data?.visits ||
    resPayload?.visits ||
    [];

  const sortedVisits = [...visits].sort((a, b) => {
    const dateA = a.visitChargeDtoList?.[0]?.dosFrom || '';
    const dateB = b.visitChargeDtoList?.[0]?.dosFrom || '';
    return dateB.localeCompare(dateA);
  });

  const [expandedVisits, setExpandedVisits] = useState({});

  useEffect(() => {
    if (sortedVisits.length > 0) {
      // First visit (latest DOS) is expanded by default
      setExpandedVisits({ [sortedVisits[0].visitId]: true });
    }
  }, [historyData]);

  const toggleExpand = (visitId) => {
    setExpandedVisits((prev) => ({
      ...prev,
      [visitId]: !prev[visitId],
    }));
  };

  const formatDos = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }
    return dateStr;
  };

  return (
    <div className={'uploadList_main_div'}>
      <Typography className={classes.heading}>Claims</Typography>
      <div className={classes.cardList}>
        {props.coderHistoryLoading ? (
          <div className={classes.loader}>
            <CircularProgress size={32} />
          </div>
        ) : sortedVisits.length > 0 ? (
          sortedVisits.map((visit) => {
            const rawDos = visit.visitChargeDtoList?.[0]?.dosFrom;
            const dosFormatted = formatDos(rawDos);
            const providerName = visit.providerName || visit.providerId || '-';
            const isExpanded = Boolean(expandedVisits[visit.visitId]);

            const cptEntries = (visit.visitChargeDtoList || [])
              .filter((charge) => Boolean(charge.cptCode))
              .map((charge) => {
                const mods = [
                  charge.modifier,
                  charge.modifier1,
                  charge.modifier2,
                  charge.modifier3,
                  charge.modifier4,
                ]
                  .map((m) => (typeof m === 'string' ? m.trim() : m))
                  .filter((m) => Boolean(m) && m !== 'NA' && m !== 'null');

                const uniqueMods = Array.from(new Set(mods));

                return { code: charge.cptCode, mods: uniqueMods };
              });

            const cptCodes = [];
            const seenCpt = new Set();
            cptEntries.forEach((entry) => {
              const key = `${entry.code}|${entry.mods.join(',')}`;
              if (!seenCpt.has(key)) {
                seenCpt.add(key);
                cptCodes.push(entry);
              }
            });

            const icdGrouped = {};
            visit.diagnosisDtoList?.forEach((diag) => {
              const versionLabel =
                diag.icdVersion === 0
                  ? 'ICD 9'
                  : diag.icdVersion === 1
                    ? 'ICD 10'
                    : `ICD ${diag.icdVersion}`;

              ['sdx1', 'sdx2', 'sdx3', 'sdx4', 'sdx5', 'sdx6', 'sdx7', 'sdx8'].forEach(
                (key) => {
                  if (diag[key]) {
                    if (!icdGrouped[versionLabel]) {
                      icdGrouped[versionLabel] = [];
                    }
                    if (!icdGrouped[versionLabel].includes(diag[key])) {
                      icdGrouped[versionLabel].push(diag[key]);
                    }
                  }
                }
              );
            });

            const statusText =
              CLAIM_STATUS_MAP[visit.claimStatus] || visit.claimStatus;

            return (
              <div key={visit.visitId} className={classes.historyCard}>
                <div
                  className={classes.historyHeader}
                  onClick={() => toggleExpand(visit.visitId)}
                  style={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    marginBottom: isExpanded ? '8px' : '0px',
                    borderBottom: isExpanded ? '1px solid #edf2f7' : 'none',
                    paddingBottom: isExpanded ? '6px' : '0px',
                  }}
                >
                  <span className={classes.headerLeft}>
                    {dosFormatted} - {providerName}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      className={classes.headerStatus}
                      style={getStatusStyle(visit.claimStatus)}
                    >
                      {statusText}
                    </span>
                    <IconButton size="small" style={{ padding: 2 }}>
                      {isExpanded ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </div>
                </div>

                <Collapse in={isExpanded} timeout="auto">
                  <div style={{ paddingTop: '4px' }}>
                    {cptCodes.length > 0 && (
                      <div className={classes.historyLine}>
                        <span className={classes.lineLabel}>CPT</span>
                        <span className={classes.lineValue}>
                          -{' '}
                          {cptCodes.map((entry, cptIdx) => (
                            <React.Fragment key={`${entry.code}-${cptIdx}`}>
                              {cptIdx > 0 ? ', ' : ''}
                              {entry.code}
                              {entry.mods.length > 0 && (
                                <Tooltip
                                  arrow
                                  title={
                                    entry.mods.length > 1
                                      ? 'Modifiers'
                                      : 'Modifier'
                                  }
                                >
                                  <span className={classes.modifier}>
                                    {' '}
                                    ({entry.mods.join(', ')})
                                  </span>
                                </Tooltip>
                              )}
                            </React.Fragment>
                          ))}
                        </span>
                      </div>
                    )}

                    {Object.entries(icdGrouped).map(([version, codes], idx) =>
                      codes.length > 0 ? (
                        <div key={idx} className={classes.historyLine}>
                          <span className={classes.lineLabel}>{version}</span>
                          <span className={classes.lineValue}>
                            - {codes.join(', ')}
                          </span>
                        </div>
                      ) : null
                    )}
                  </div>
                </Collapse>
              </div>
            );
          })
        ) : (
          <div className={classes.noData}>No Claims found</div>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  coderHistoryData: state.coder?.coderHistory,
  coderHistoryLoading: state.coder?.coderHistoryLoading,
  coderData: state.coder,
});

const mapDispatchToProps = (dispatch) => ({
  getCoderHistory: (values) => dispatch(getCoderHistory(values)),
});

CoderHistoryLib.propTypes = {
  getCoderHistory: PropTypes.func,
  rowInfo: PropTypes.object,
  applicationId: PropTypes.number,
  coderHistoryData: PropTypes.object,
  coderHistoryLoading: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(CoderHistoryLib);
